// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState, useEffect } from 'react';

import { type UpdateWorkOrderBody, type WorkOrder, useEvolveGet } from '@evolvemep/api-ts';

import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

/**
 * When the user opens a Work Order, if the WO is in Submitted, it automatically moves to Pending
 * (assuming the user has permission).
 */
const useSetWorkOrderAsPending = (workOrder: WorkOrder, onSetAsPending: () => Promise<void>) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const { setWorkOrderAsPending } = useWorkOrders();
  const [settingAsPending, setSettingAsPending] = useState(false);
  useEffect(() => {
    if (workOrder.workOrderStateName === 'Submitted' && canEditWorkOrder('setAsPending')) {
      setSettingAsPending(true);
      void setWorkOrderAsPending(workOrder.workOrderId)
        .then(onSetAsPending)
        .finally(() => setSettingAsPending(false));
    }
  }, [workOrder.workOrderId, workOrder.workOrderStateName, setWorkOrderAsPending, onSetAsPending, canEditWorkOrder]);
  return { settingAsPending };
};

/**
 * Shared update/refresh/permission plumbing for a Work Order detail view — used by both the desktop
 * and mobile detail pages so `editable`/`saving` stay derived the same way in both places.
 */
export const useWorkOrderDetailMutations = (
  workOrder: WorkOrder,
  onWorkOrderUpdated: (workOrder: WorkOrder) => void,
) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const { updateWorkOrder: patchWorkOrder, mutationRunning: savingWorkOrder } = useWorkOrders();

  const updateWorkOrder = useCallback(
    (body: UpdateWorkOrderBody) => patchWorkOrder(workOrder.workOrderId, body).then(onWorkOrderUpdated),
    [onWorkOrderUpdated, patchWorkOrder, workOrder.workOrderId],
  );

  const { apiCall: getWorkOrderApiCall, loading } = useEvolveGet(`shop/workOrder/${workOrder.workOrderId}`);
  const refreshWorkOrder = useCallback(
    async () => getWorkOrderApiCall().then(onWorkOrderUpdated),
    [onWorkOrderUpdated, getWorkOrderApiCall],
  );

  const { settingAsPending } = useSetWorkOrderAsPending(workOrder, refreshWorkOrder);

  const saving = loading || savingWorkOrder || settingAsPending;
  const editable = canEditWorkOrder('editProperties') && !saving;

  return { workOrder, updateWorkOrder, refreshWorkOrder, editable, saving, loading, canEditWorkOrder };
};
